import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AlertTriangle, CreditCard, LogOut } from 'lucide-react';
import { subscriptionPrice } from '../../utils/currency';
import ModalShell, { MODAL_CANCEL_CLASS, MODAL_SUBMIT_CLASS } from '../common/ModalShell';

// Full-screen, non-dismissable block. Two cases:
//   - expired plan, no grace → renew.
//   - admin set a pay-to-activate plan change → pay to activate.
// The only ways out are My Subscription or logging out. AppLayout hides it on
// the /mySubscription route so the plan page stays reachable.
//
// Since 22 Sep 2026 (Q8) there is no gateway, so the button no longer promises
// a payment screen: it takes the doctor to their plan, where the amount, the
// invoice reference and how to settle it are written down.
const SubscriptionLockModal = () => {
  const navigate = useNavigate();
  const sub = useSelector((s) => s.subscription.mySubscription);
  const pending = sub?.paymentRequired ? sub.pending : null;

  const logout = () => {
    localStorage.clear();
    window.location.replace('/');
  };

  return (
    <ModalShell
      title={pending ? 'Your plan was changed' : 'Your subscription has expired'}
      size="md"
      zClass="z-[200]"
      testId="subscription-lock"
      footer={(
        <>
          <button type="button" onClick={logout} className={`${MODAL_CANCEL_CLASS} flex items-center gap-2`}>
            <LogOut className="w-4 h-4" /> Log out
          </button>
          <button
            type="button"
            onClick={() => navigate('/mySubscription')}
            data-testid="lock-renew"
            className={MODAL_SUBMIT_CLASS}
          >
            <CreditCard className="w-4 h-4" /> {pending ? 'See what’s owed' : 'Renew now'}
          </button>
        </>
      )}
    >
      <div className="text-center">
        <div className={`mx-auto mb-4 w-14 h-14 rounded-2xl flex items-center justify-center ${pending ? 'bg-[#4ABDBD]/15 text-[#2D8A8A]' : 'bg-red-100 text-red-600'}`}>
          {pending ? <CreditCard className="w-7 h-7" /> : <AlertTriangle className="w-7 h-7" />}
        </div>
        <p className="text-sm text-slate-500">
          {pending ? (
            <>
              Your administrator set you to <strong>{pending.packageName}</strong>
              {pending.periodLabel ? ` (${pending.periodLabel})` : ''}. Pay <strong>{subscriptionPrice(pending.due, pending, { grouped: true })}</strong> to activate it
              {pending.credit > 0 ? <> — {subscriptionPrice(pending.credit, pending, { grouped: true })} of unused value from your current plan is credited</> : null}.
            </>
          ) : (
            <>
              {sub?.packageName ? <>Your <strong>{sub.packageName}</strong> plan has ended. </> : null}
              Renew or upgrade to keep using Mitra&nbsp;One&nbsp;Care. If you have an appointment to manage,
              your administrator can grant you temporary access.
            </>
          )}
        </p>
      </div>
    </ModalShell>
  );
};

export default SubscriptionLockModal;
